import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faLocationDot,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getDuplicateAthleteComparison } from "../../../services/AdminService";
import ConfirmModal from "../../../components/ConfirmModel";
import {
  COLUMNS,
  computeGroupMatches,
  extractBackendMessage,
} from "./AdminDuplicateUsers";

const formatCombineDate = (dateUTC) => {
  if (!dateUTC?.year || !dateUTC?.month || !dateUTC?.day) return "";
  return `${String(dateUTC.month).padStart(2, "0")}/${String(
    dateUTC.day,
  ).padStart(2, "0")}/${dateUTC.year}`;
};

const getCombinesFor = (compareData, userId) => {
  if (!compareData || !userId) return [];
  if (compareData.firstAccount?.userId === userId) {
    return compareData.firstAccount.combines ?? [];
  }
  if (compareData.secondAccount?.userId === userId) {
    return compareData.secondAccount.combines ?? [];
  }
  return [];
};

function CombineList({ combines }) {
  if (!combines.length) {
    return <p className="text-xs text-gray-400 italic">No combines attended</p>;
  }
  return (
    <div className="space-y-1.5">
      {combines.map((c) => (
        <div
          key={c.eventId}
          className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5"
        >
          <p className="text-xs font-medium text-gray-800 truncate">{c.name}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <FontAwesomeIcon icon={faLocationDot} className="text-gray-400" />
            {[c.city, c.state].filter(Boolean).join(", ")}
          </p>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <FontAwesomeIcon icon={faCalendarDays} className="text-gray-400" />
            {formatCombineDate(c.dateUTC)}
          </p>
        </div>
      ))}
    </div>
  );
}

function Avatar({ src, alt }) {
  return (
    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-200 mx-auto">
      <img
        src={src || "/default-profile-img.png"}
        alt={alt}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export default function DuplicateAccountCompareModal({
  open,
  primary,
  secondary,
  merging,
  mergeError,
  onClose,
  onMerge,
}) {
  const apiInstance = useAxiosInstance();
  const [compareData, setCompareData] = useState(null);
  const [loadingCombines, setLoadingCombines] = useState(false);
  const [combinesError, setCombinesError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const primaryId = primary?.userId;
  const secondaryId = secondary?.userId;

  useEffect(() => {
    if (!open || !primaryId || !secondaryId) {
      setCompareData(null);
      setCombinesError(null);
      return;
    }
    let cancelled = false;
    setLoadingCombines(true);
    setCombinesError(null);
    getDuplicateAthleteComparison(apiInstance, primaryId, secondaryId)
      .then((data) => {
        if (!cancelled) setCompareData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setCombinesError(
            extractBackendMessage(err, "Failed to load combine history."),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCombines(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, primaryId, secondaryId, apiInstance]);

  useEffect(() => {
    if (!open) setConfirmOpen(false);
  }, [open]);

  if (!open || !primary || !secondary) return null;

  // Computed for just this pair, not the whole name-group — a group of 3+
  // would otherwise mislabel fields where only the selected two agree.
  const matches = computeGroupMatches([primary, secondary], COLUMNS);

  // Always returns to the compare view; the parent decides whether to also
  // close this whole dialog (on success) or keep it open with mergeError set.
  const handleConfirmMerge = async () => {
    await onMerge();
    setConfirmOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white w-[95%] sm:w-[90%] md:w-full max-w-3xl rounded-md relative max-h-[90vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-base z-10"
        >
          <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
        </button>

        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b px-4 py-3">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            Compare Accounts
          </h2>
        </div>

        <div
          className="px-3 py-3 flex-1 overflow-y-auto"
          style={{ maxHeight: "70vh" }}
        >
          <div className="grid grid-cols-3 gap-0 min-w-[500px] text-sm">
            <div></div>
            <div className="text-center pb-2">
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-100 text-green-700 mb-1">
                Kept
              </span>
              <Avatar
                src={primary.profilePicture}
                alt={`${primary.firstName} ${primary.lastName}`}
              />
              <h3 className="font-semibold mt-2 text-blue-700 text-sm">
                {primary.firstName} {primary.lastName}
              </h3>
              <p className="text-gray-500 text-xs">{primary.username}</p>
              <p className="text-gray-400 text-[10px]">
                User #{primary.userId}
              </p>
            </div>
            <div className="text-center pb-2">
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700 mb-1">
                Deleted
              </span>
              <Avatar
                src={secondary.profilePicture}
                alt={`${secondary.firstName} ${secondary.lastName}`}
              />
              <h3 className="font-semibold mt-2 text-blue-700 text-sm">
                {secondary.firstName} {secondary.lastName}
              </h3>
              <p className="text-gray-500 text-xs">{secondary.username}</p>
              <p className="text-gray-400 text-[10px]">
                User #{secondary.userId}
              </p>
            </div>

            {COLUMNS.map((col) => {
              const isMatch = matches[col.key];
              return (
                <React.Fragment key={col.key}>
                  <div
                    className={`py-2 border-b text-gray-700 ${
                      isMatch ? "bg-green-50 border-green-200" : ""
                    }`}
                  >
                    {col.label}
                  </div>
                  <div
                    className={`py-2 border text-center ${
                      isMatch
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-red-50 border-red-200 text-red-700"
                    }`}
                  >
                    {primary[col.key] ?? ""}
                  </div>
                  <div
                    className={`py-2 border text-center ${
                      isMatch
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-red-50 border-red-200 text-red-700"
                    }`}
                  >
                    {secondary[col.key] ?? ""}
                  </div>
                </React.Fragment>
              );
            })}

            {/* Same 3-col grid as above, so each account's combines line up under it. */}
            <div className="pt-3 pb-1 border-t-2 border-gray-300 text-gray-700 font-semibold text-sm">
              Combine History
            </div>
            <div className="col-span-2 pt-3 pb-1 border-t-2 border-gray-300">
              {loadingCombines && (
                <p className="text-xs text-gray-500">
                  Loading combine history...
                </p>
              )}
              {combinesError && !loadingCombines && (
                <p className="text-xs text-red-500">{combinesError}</p>
              )}
            </div>
            {!loadingCombines && !combinesError && (
              <>
                <div></div>
                <div className="pb-2 pr-2">
                  <CombineList
                    combines={getCombinesFor(compareData, primary.userId)}
                  />
                </div>
                <div className="pb-2 pl-2">
                  <CombineList
                    combines={getCombinesFor(compareData, secondary.userId)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {mergeError && (
          <div className="mx-3 mb-2 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {mergeError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="px-4 py-2 rounded bg-red-500 text-white text-sm hover:bg-red-600"
          >
            Merge Accounts
          </button>
        </div>
      </div>

      <ConfirmModal
        show={confirmOpen}
        title="Merge accounts permanently"
        message={
          <>
            This action <strong>cannot be undone</strong>.{" "}
            <strong>
              {secondary.firstName} {secondary.lastName}
            </strong>{" "}
            (login email: <strong>{secondary.username}</strong>) will be
            permanently deleted — its account, and login access are removed and
            cannot be recovered.{" "}
            <strong>
              {primary.firstName} {primary.lastName}
            </strong>{" "}
            (login email: <strong>{primary.username}</strong>) will be kept. All
            combine results, videos, measurements and other related data will be
            moved to <strong>{primary.username}</strong>.
          </>
        }
        confirmLabel={merging ? "Merging..." : "Merge accounts"}
        confirmDisabled={merging}
        onConfirm={handleConfirmMerge}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
